import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Globe, Loader2, Receipt, Save, Store } from 'lucide-react';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { TabSkeleton } from '../BotManagement/BotManagementSkeleton';
import { CHECKOUT_NAME_MAX, checkoutNameError } from '../../../utils/checkoutName';
import CheckoutAvatar from './CheckoutAvatar';
import PaymentSection, { OptionRow } from '../PixConfig/PaymentSection';

/**
 * Aba "Checkout": o que aparece na página de checkout da Spectrum (cartão do
 * Global, SPEI/Nequi/Chile do LATAM): foto, nome e número do pedido. Vale para
 * todos os mercados do bot.
 */
export default function CheckoutConfig() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();

    const [loadingPage, setLoadingPage] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasAvatar, setHasAvatar] = useState(false);
    const [name, setName] = useState('');
    const [botName, setBotName] = useState('');
    const [showOrder, setShowOrder] = useState(false);

    useEffect(() => {
        if (!botId) return;
        let alive = true;
        setLoadingPage(true);
        authService.bots.edit_bot(botId)
            .then(({ data }) => {
                if (!alive) return;
                const pc = data.bot?.pix_config || {};
                setHasAvatar(!!pc.has_checkout_avatar);
                setName(pc.checkout_name || '');
                setShowOrder(pc.checkout_show_order === true);
                setBotName(data.bot?.bot_name || data.bot?.bot_username || '');
            })
            .catch(() => alive && showError('Erro ao carregar o checkout'))
            .finally(() => alive && setLoadingPage(false));
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nameError = checkoutNameError(name);
        if (nameError) {
            showError(nameError);
            return;
        }
        setSaving(true);
        try {
            await authService.bots.save_pix_config(botId, {
                checkout_name: name.trim(),
                checkout_show_order: showOrder,
            });
            showSuccess('Checkout salvo!');
        } catch (err) {
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    if (loadingPage) return <TabSkeleton tab="checkout" />;

    return (
        <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
            <div className="spt-form-bot">
                <div className="flex justify-end mb-5">
                    <span className="pset-chip"><Globe size={12} /> Vale em todos os mercados do bot</span>
                </div>

                <div className="pset-stack">
                    <PaymentSection icon={Store} title="Identidade" subtitle="O topo da página de pagamento.">
                        <div className="grid gap-6 md:grid-cols-2 items-start">
                            {/* Foto: salva na hora, fora do "Salvar" */}
                            <CheckoutAvatar botId={botId} hasAvatar={hasAvatar} />
                            <div>
                                <div className="pset-field-row">
                                    <label className="pset-field-label" htmlFor="checkout-name">Nome</label>
                                    <span className="pset-counter">{name.trim().length}/{CHECKOUT_NAME_MAX}</span>
                                </div>
                                <input
                                    id="checkout-name"
                                    aria-label="Nome no checkout"
                                    className="input3"
                                    value={name}
                                    maxLength={CHECKOUT_NAME_MAX}
                                    placeholder={botName || 'Nome do bot'}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <p className="pset-option-hint mt-2">Vazio usa o nome do bot.</p>
                            </div>
                        </div>
                    </PaymentSection>

                    <PaymentSection icon={Receipt} title="Pedido" subtitle="O que aparece no resumo da compra.">
                        <div className="pset-options">
                            <OptionRow
                                title="Mostrar número do pedido"
                                label="Mostrar número do pedido no checkout"
                                hint='Ligado: "Order #123", como a Stripe. Desligado: o nome do plano.'
                                checked={showOrder}
                                onChange={setShowOrder}
                            />
                        </div>
                    </PaymentSection>
                </div>

                <div className="pset-footer">
                    <button disabled={saving} className="btn-primary inline-flex items-center">
                        {saving
                            ? <><Loader2 size={14} className="animate-spin me-2" />Salvando...</>
                            : <><Save size={14} className="me-2" />Salvar checkout</>}
                    </button>
                </div>
            </div>
        </form>
    );
}
